import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  dateTime, type Draw, type League, type LeagueRegistration, type StandingRow, type Team,
} from '@lcc/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Card, CheckField, Empty, ErrorNote, Loading, Modal, Status, Tabs, TextField,
  errorMessage, useToast,
} from '../components/ui';
import { PageHeader } from '../App';
import { drawTimesLabel } from './Leagues';

type Tab = 'teams' | 'registrations' | 'schedule' | 'standings';

interface StandingsResponse {
  league: { id: string; name: string };
  schedules: Array<{ scheduleNo: number; standings: StandingRow[] }>;
}

export function LeagueDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('teams');
  const [scheduleNo, setScheduleNo] = useState(1);
  const [drawing, setDrawing] = useState(false);
  const [generating, setGenerating] = useState(false);

  const league = useQuery({
    queryKey: ['league', id],
    queryFn: () => api.get<{ data: League }>(`/leagues/${id}`).then((r) => r.data),
  });

  const teams = useQuery({
    queryKey: ['teams', id, scheduleNo],
    queryFn: () => api.get<{ data: Team[] }>(`/teams?leagueId=${id}&scheduleNo=${scheduleNo}`)
      .then((r) => r.data),
  });

  const registrations = useQuery({
    queryKey: ['registrations', id],
    queryFn: () => api.get<{ data: LeagueRegistration[] }>(`/leagues/${id}/registrations`)
      .then((r) => r.data),
  });

  const draws = useQuery({
    queryKey: ['draws', id, scheduleNo],
    queryFn: () => api.get<{ data: Draw[] }>(`/draws?leagueId=${id}&scheduleNo=${scheduleNo}`)
      .then((r) => r.data),
  });

  const standings = useQuery({
    queryKey: ['standings', id],
    queryFn: () => api.get<StandingsResponse>(`/standings/${id}`),
  });

  if (league.isLoading) return <><PageHeader title="League" /><div className="content"><Loading /></div></>;
  if (league.error) return <><PageHeader title="League" /><div className="content"><ErrorNote error={league.error} /></div></>;

  const l = league.data!;
  const spares = registrations.data?.filter((r) => r.isSpare) ?? [];
  const pool = registrations.data?.filter((r) => !r.isSpare) ?? [];

  return (
    <>
      <PageHeader
        title={l.name}
        sub={<><Link to="/leagues">Leagues</Link> · {drawTimesLabel(l)} · {l.endsPerGame} ends</>}
        actions={can('convenor') && (
          <>
            <button className="btn" type="button" onClick={() => setDrawing(true)}>Draw teams</button>
            <button className="btn btn--accent" type="button" onClick={() => setGenerating(true)}>
              Generate schedule
            </button>
          </>
        )}
      />

      <div className="content">
        {l.schedulesPerSeason > 1 && (
          <div className="row" style={{ marginBottom: '1rem' }}>
            <span className="small muted">Schedule</span>
            {Array.from({ length: l.schedulesPerSeason }, (_, i) => i + 1).map((n) => (
              <button key={n} type="button"
                className={n === scheduleNo ? 'btn btn--sm btn--primary' : 'btn btn--sm'}
                onClick={() => setScheduleNo(n)}>{n}</button>
            ))}
          </div>
        )}

        <Tabs
          active={tab} onChange={setTab}
          tabs={[
            { id: 'teams', label: 'Teams', count: teams.data?.length },
            { id: 'registrations', label: 'Registrations', count: registrations.data?.length },
            { id: 'schedule', label: 'Schedule', count: draws.data?.length },
            { id: 'standings', label: 'Standings' },
          ]}
        />

        {tab === 'teams' && (
          teams.isLoading ? <Loading /> :
          !teams.data?.length
            ? <Card><Empty title={`No teams in schedule ${scheduleNo}`}>
                {l.format === 'skips_entry'
                  ? 'Skips enter their own teams. Add them, or draw from the registration pool.'
                  : 'Draw teams from the registration pool to get started.'}
              </Empty></Card>
            : (
              <div className="grid grid--3">
                {teams.data.map((t) => (
                  <Card key={t.id} title={t.name}>
                    <table className="t" style={{ fontSize: '.85rem' }}>
                      <tbody>
                        {t.players.map((p) => (
                          <tr key={p.memberId}>
                            <td style={{ padding: '.3rem 0' }}>
                              <Link to={`/members/${p.memberId}`}>{p.fullName}</Link>
                            </td>
                            <td className="right muted small">{p.position}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {t.players.length === 0 && <p className="small muted">No players yet.</p>}
                  </Card>
                ))}
              </div>
            )
        )}

        {tab === 'registrations' && (
          registrations.isLoading ? <Loading /> : (
            <div className="stack">
              <Card title={`Registration pool (${pool.length})`} flush>
                {pool.length === 0 ? <Empty title="Nobody has registered yet" /> : (
                  <div className="table-wrap">
                    <table className="t">
                      <thead>
                        <tr><th>Member</th><th>Prefers</th><th className="num">Skill</th>
                          <th>Entered as</th><th>Team</th></tr>
                      </thead>
                      <tbody>
                        {pool.map((r) => (
                          <tr key={r.id}>
                            <td><Link to={`/members/${r.memberId}`}>{r.fullName}</Link></td>
                            <td className="muted small">{r.positionPref ?? 'any'}</td>
                            <td className="num">{r.skillRating ?? '—'}</td>
                            <td className="muted small">{r.entryMode}</td>
                            <td className="muted small">
                              {r.teamId
                                ? teams.data?.find((t) => t.id === r.teamId)?.name ?? 'assigned'
                                : <span className="pill pill--quiet">unassigned</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <Card title={`Spares (${spares.length})`} flush>
                {spares.length === 0 ? <Empty title="No spares signed up">
                  Every league needs spares — worth asking at the next draw.
                </Empty> : (
                  <div className="table-wrap">
                    <table className="t">
                      <thead><tr><th>Member</th><th>Prefers</th><th>Notes</th></tr></thead>
                      <tbody>
                        {spares.map((r) => (
                          <tr key={r.id}>
                            <td><Link to={`/members/${r.memberId}`}>{r.fullName}</Link></td>
                            <td className="muted small">{r.positionPref ?? 'any'}</td>
                            <td className="muted small">{r.notes ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          )
        )}

        {tab === 'schedule' && (
          draws.isLoading ? <Loading /> :
          !draws.data?.length
            ? <Card><Empty title="No schedule yet">
                Use <b>Generate schedule</b> to build a round robin across the club's sheets.
              </Empty></Card>
            : (
              <div className="stack">
                {draws.data.map((d) => (
                  <Card key={d.id}
                    title={<h3>Draw {d.drawNumber} <span className="muted small" style={{ fontWeight: 400 }}>
                      {dateTime(d.scheduledAt)}</span></h3>}
                    actions={<Status value={d.status} />} flush>
                    <div className="table-wrap">
                      <table className="t">
                        <thead>
                          <tr><th>Sheet</th><th>Home</th><th>Away</th>
                            <th className="num">Score</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                          {d.games.map((g) => (
                            <tr key={g.id}>
                              <td className="mono">{g.sheetNumber ?? '—'}</td>
                              <td>{g.homeTeamName ?? <span className="muted">bye</span>}</td>
                              <td>{g.awayTeamName ?? <span className="muted">bye</span>}</td>
                              <td className="num">
                                {g.homeScore !== null && g.awayScore !== null
                                  ? `${g.homeScore} – ${g.awayScore}` : '—'}
                              </td>
                              <td><Status value={g.status} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                ))}
                <p className="small muted">
                  Scores are entered on the <Link to="/schedule">Draws &amp; scores</Link> screen.
                </p>
              </div>
            )
        )}

        {tab === 'standings' && (
          standings.isLoading ? <Loading /> :
          !standings.data?.schedules.length
            ? <Card><Empty title="No results yet">
                Standings appear as soon as games are scored.
              </Empty></Card>
            : (
              <div className="stack">
                {standings.data.schedules.map((s) => (
                  <Card key={s.scheduleNo}
                    title={l.schedulesPerSeason > 1 ? `Schedule ${s.scheduleNo}` : 'Standings'} flush>
                    <div className="table-wrap">
                      <table className="t">
                        <thead>
                          <tr>
                            <th>#</th><th>Team</th>
                            <th className="num">P</th><th className="num">W</th>
                            <th className="num">L</th><th className="num">T</th>
                            <th className="num">Pts</th><th className="num">For</th>
                            <th className="num">Against</th><th className="num">+/−</th>
                            <th className="num">Ends</th>
                          </tr>
                        </thead>
                        <tbody>
                          {s.standings.map((r) => (
                            <tr key={r.teamId}>
                              <td className="mono">{r.rank}</td>
                              <td><b>{r.teamName}</b></td>
                              <td className="num">{r.played}</td>
                              <td className="num">{r.won}</td>
                              <td className="num">{r.lost}</td>
                              <td className="num">{r.tied}</td>
                              <td className="num"><b>{r.points}</b></td>
                              <td className="num">{r.pointsFor}</td>
                              <td className="num">{r.pointsAgainst}</td>
                              <td className="num">{r.plusMinus > 0 ? `+${r.plusMinus}` : r.plusMinus}</td>
                              <td className="num">{r.endsWon}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                ))}
              </div>
            )
        )}
      </div>

      {drawing && (
        <DrawTeamsModal league={l} scheduleNo={scheduleNo} onClose={() => setDrawing(false)} />
      )}
      {generating && (
        <GenerateScheduleModal league={l} scheduleNo={scheduleNo} teamCount={teams.data?.length ?? 0}
          onClose={() => setGenerating(false)} />
      )}
    </>
  );
}

/* ------------------------------------------------------------- draw teams */

function DrawTeamsModal(
  { league, scheduleNo, onClose }: { league: League; scheduleNo: number; onClose: () => void },
) {
  const qc = useQueryClient();
  const toast = useToast();
  const [teamSize, setTeamSize] = useState('4');
  const [balance, setBalance] = useState(true);
  const [replace, setReplace] = useState(true);
  const [pattern, setPattern] = useState('Team {skip}');

  const draw = useMutation({
    mutationFn: () => api.post<{
      data: Team[];
      summary: { teamsCreated: number; playersPlaced: number; unplaced: number;
                 heldBackForWaivers: string[]; note?: string; waiverNote?: string };
    }>(`/teams/draw/${league.id}`, {
      scheduleNo, teamSize: Number(teamSize), balanceBySkill: balance,
      namePattern: pattern, replaceExisting: replace,
    }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['teams', league.id] });
      void qc.invalidateQueries({ queryKey: ['registrations', league.id] });
      void qc.invalidateQueries({ queryKey: ['league', league.id] });
      toast.success(`${r.summary.teamsCreated} teams drawn.`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const summary = draw.data?.summary;

  return (
    <Modal
      title={`Draw teams — schedule ${scheduleNo}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {summary ? 'Done' : 'Cancel'}
          </button>
          <button className="btn btn--accent" type="button"
            disabled={draw.isPending} onClick={() => draw.mutate()}>
            {draw.isPending ? 'Drawing…' : summary ? 'Draw again' : 'Draw teams'}
          </button>
        </>
      }
    >
      <div className="stack">
        {draw.error ? <ErrorNote error={draw.error} /> : null}

        {summary ? (
          <div className="stack">
            <div className="banner banner--good">
              <strong>{summary.teamsCreated} teams drawn</strong> — {summary.playersPlaced} players placed.
            </div>
            {summary.note && <div className="banner banner--warn">{summary.note}</div>}
            {summary.waiverNote && (
              <div className="banner banner--warn">
                {summary.waiverNote}
                <ul style={{ margin: '.35rem 0 0', paddingLeft: '1.1rem' }}>
                  {summary.heldBackForWaivers.map((n) => <li key={n}>{n}</li>)}
                </ul>
              </div>
            )}
            <div className="grid grid--2">
              {draw.data!.data.map((t) => (
                <div key={t.id} className="card" style={{ padding: '.7rem .9rem' }}>
                  <b>{t.name}</b>
                  <ul className="small muted" style={{ margin: '.3rem 0 0', paddingLeft: '1.1rem' }}>
                    {t.players.map((p) => (
                      <li key={p.memberId}>{p.fullName} <i>({p.position})</i></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <fieldset disabled={draw.isPending}>
            <p className="small muted">
              Builds teams from the {league.registrationCount ?? 0} members registered to this
              league. Members with outstanding waivers are held back rather than blocking the draw.
            </p>
            <div className="grid grid--2">
              <TextField label="Players per team" type="number" value={teamSize} onChange={setTeamSize} />
              <TextField label="Team name pattern" value={pattern} onChange={setPattern}
                hint="{skip} becomes the skip's surname." />
            </div>
            <CheckField label="Balance teams by skill rating" checked={balance} onChange={setBalance}
              hint="Deals strongest to weakest across teams instead of drawing at random." />
            <CheckField label={`Replace existing teams in schedule ${scheduleNo}`}
              checked={replace} onChange={setReplace}
              hint="Refused if any game in this schedule already has a result." />
          </fieldset>
        )}
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------- generate schedule */

interface PreviewDraw {
  drawNumber: number;
  scheduledAt: string;
  games: Array<{ sheetNumber: number; round: number; home: string; away: string }>;
}
interface GenerateResponse {
  dryRun?: boolean;
  summary?: { league: string; teams: number; draws: number; games: number;
              firstDraw: string | null; lastDraw: string | null };
  draws?: PreviewDraw[];
  created?: number;
}

function GenerateScheduleModal(
  { league, scheduleNo, teamCount, onClose }:
  { league: League; scheduleNo: number; teamCount: number; onClose: () => void },
) {
  const qc = useQueryClient();
  const toast = useToast();
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [games, setGames] = useState('7');
  const [sheets, setSheets] = useState('1,2,3,4,5,6');
  const [blackout, setBlackout] = useState('');
  const [replace, setReplace] = useState(false);
  const [preview, setPreview] = useState<GenerateResponse | null>(null);

  const payload = () => ({
    leagueId: league.id,
    scheduleNo,
    startDate,
    gamesPerTeam: Number(games),
    sheetNumbers: sheets.split(',').map((s) => Number(s.trim())).filter((n) => n > 0),
    blackoutDates: blackout.split(',').map((s) => s.trim()).filter(Boolean),
    replaceExisting: replace,
  });

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api.post<GenerateResponse>('/draws/generate', { ...payload(), dryRun }),
    onSuccess: (r, dryRun) => {
      if (dryRun) { setPreview(r); return; }
      void qc.invalidateQueries({ queryKey: ['draws'] });
      void qc.invalidateQueries({ queryKey: ['standings', league.id] });
      toast.success(`${r.created} draws created.`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Modal
      title={`Generate schedule — ${league.name}`}
      wide
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn" type="button" disabled={run.isPending}
            onClick={() => run.mutate(true)}>
            {run.isPending ? 'Working…' : 'Preview'}
          </button>
          <button className="btn btn--accent" type="button"
            disabled={run.isPending || !preview}
            onClick={() => run.mutate(false)}>
            Create {preview?.summary?.draws ?? ''} draws
          </button>
        </>
      }
    >
      <div className="stack">
        {run.error ? <ErrorNote error={run.error} /> : null}

        {teamCount < 2 && (
          <div className="banner banner--warn">
            This schedule has {teamCount} team{teamCount === 1 ? '' : 's'}. Create or draw the
            teams first.
          </div>
        )}

        <fieldset disabled={run.isPending}>
          <div className="grid grid--2">
            <TextField label="First possible date" type="date" value={startDate} onChange={setStartDate}
              hint={`Draws land on ${drawTimesLabel(league)}.`} />
            <TextField label="Games per team" type="number" value={games} onChange={setGames}
              hint={`${teamCount} teams — a full rotation is ${Math.max(0, teamCount - 1)} games.`} />
          </div>
          <TextField label="Sheets" value={sheets} onChange={setSheets}
            hint="Comma separated. The club has six." />
          <TextField label="Blackout dates" value={blackout} onChange={setBlackout}
            hint="Comma separated YYYY-MM-DD — Christmas week, a bonspiel weekend." />
          <CheckField label="Replace the existing schedule" checked={replace} onChange={setReplace}
            hint="Refused if any game already has a result." />
        </fieldset>

        {preview?.summary && (
          <>
            <div className="banner banner--info">
              <strong>{preview.summary.draws} draws, {preview.summary.games} games</strong> for{' '}
              {preview.summary.teams} teams, from {preview.summary.firstDraw?.replace('T', ' ')} to{' '}
              {preview.summary.lastDraw?.replace('T', ' ')}. Nothing has been saved yet.
            </div>
            <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
              <table className="t">
                <thead><tr><th>Draw</th><th>When</th><th>Sheet</th><th>Home</th><th>Away</th></tr></thead>
                <tbody>
                  {preview.draws?.flatMap((d) => d.games.map((g, i) => (
                    <tr key={`${d.drawNumber}-${g.sheetNumber}`}>
                      <td className="mono">{i === 0 ? d.drawNumber : ''}</td>
                      <td className="small nowrap">{i === 0 ? d.scheduledAt.replace('T', ' ') : ''}</td>
                      <td className="mono">{g.sheetNumber}</td>
                      <td className="small">{g.home}</td>
                      <td className="small">{g.away}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
